import { Button, ChakraProps, HStack, Spinner } from '@chakra-ui/react';
import { MdOutlineCancel } from 'react-icons/md';
import { FC, useMemo } from 'react';
import { Token } from '@app/types/token';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useAddress, useSigner } from '@thirdweb-dev/react';
import { useGetRevealWindowForBlindAuction } from '@app/hooks/nft/useGetRevealWindowForBlindAuction';
import { useHasActiveBids } from '@app/hooks/nft/useHasActiveBids';
import { useCancelListing } from '@app/hooks/nft/useCancelListing';

type Props = {
  listing: NFTMarketplaceListing;
  sellCurrency: Token | undefined;
  variant?: 'rowCompact' | 'secondary' | 'primary';
} & ChakraProps;

export const CancelListingButton: FC<Props> = ({
  sellCurrency,
  listing,
  variant = 'rowCompact',
  ...rest
}) => {
  const address = useAddress();
  const { data: revealWindow } = useGetRevealWindowForBlindAuction();
  const { data: hasActiveBid } = useHasActiveBids(listing.id);
  const { mutate: cancelListing, isLoading: isCancelling } = useCancelListing();
  const signer = useSigner();

  const isAvailable = useMemo(() => {
    if (!listing || !listing.isNFTAvailable) {
      return false;
    }

    if (
      address?.toLowerCase() !== listing.maker?.toLowerCase() ||
      !listing.isNFTAvailable
    ) {
      return false;
    }

    switch (listing.type) {
      case 'BUY_IT_NOW':
      case 'BUY_IT_NOW_WITH_BIDS': {
        return true;
      }
      case 'ENGLISH_AUCTION':
      case 'DUTCH_AUCTION':
      case 'BLIND': {
        const now = Date.now();

        if (now >= listing.start * 1000 && now < listing.end * 1000) {
          return false;
        }

        if (
          listing.type === 'BLIND' &&
          now < listing.end * 1000 + revealWindow
        ) {
          return false;
        }

        if (
          (listing.type === 'ENGLISH_AUCTION' || listing.type === 'BLIND') &&
          hasActiveBid
        ) {
          return false;
        }

        return true;
      }
      default: {
        return false;
      }
    }
  }, [listing, address, revealWindow, hasActiveBid]);

  if (!isAvailable) {
    return null;
  }

  return (
    <HStack gap={0}>
      <Button
        width="100%"
        variant={variant}
        leftIcon={
          isCancelling ? (
            <Spinner width={3} height={3} />
          ) : (
            <MdOutlineCancel size={16} />
          )
        }
        isDisabled={isCancelling}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();

          cancelListing({
            listingId: listing.id,
            signer
          });
        }}
        {...rest}
      >
        {isCancelling ? 'Processing' : 'Cancel'}
      </Button>
    </HStack>
  );
};
